import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/layout";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { Table, message, Input, Button, Typography, Avatar } from "antd";
import moment from "moment";
import {
  SearchOutlined, FilterOutlined, ClearOutlined,
  UserOutlined, StopOutlined, TeamOutlined,
  MedicineBoxOutlined, SettingOutlined, CheckCircleOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Role config ─────────────────────────────────────────────── */
const ROLE_CONFIG = {
  admin:   { color: "#7c3aed", bg: "#f5f3ff", icon: <SettingOutlined /> },
  doctor:  { color: EMERALD,   bg: TEAL_LIGHT, icon: <MedicineBoxOutlined /> },
  patient: { color: "#2563eb", bg: "#eff6ff",  icon: <UserOutlined /> },
  staff:   { color: "#b45309", bg: "#fef9c3",  icon: <TeamOutlined /> },
};
const getRoleCfg = (r) => ROLE_CONFIG[r?.toLowerCase()] || { color: MUTED, bg: SURFACE, icon: <UserOutlined /> };

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("staff-list-styles")) return;
  const el = document.createElement("style");
  el.id = "staff-list-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .sl2-root { font-family:'DM Sans',sans-serif; background:${SURFACE}; min-height:100vh; }
    .sl2-wrap { max-width:1200px; margin:0 auto; padding:32px 24px 64px; }

    /* Hero */
    .sl2-hero {
      background:linear-gradient(135deg,${SLATE} 0%,${SLATE_MID} 100%);
      border-radius:24px; padding:36px 44px;
      position:relative; overflow:hidden; margin-bottom:28px;
    }
    .sl2-hero::before {
      content:''; position:absolute; top:-80px; right:-80px;
      width:300px; height:300px; border-radius:50%;
      background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);
      filter:blur(32px); pointer-events:none;
    }
    .sl2-hero::after {
      content:''; position:absolute; bottom:-60px; left:-40px;
      width:200px; height:200px; border-radius:50%;
      background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);
      filter:blur(26px); pointer-events:none;
    }
    .sl2-eyebrow {
      display:inline-block; padding:4px 12px; border-radius:20px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
      font-family:'Sora',sans-serif; margin-bottom:12px;
    }
    .sl2-hero-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
    .sl2-hero-badge {
      background:rgba(255,255,255,0.12); border-radius:20px;
      padding:4px 12px; font-size:12px; color:rgba(255,255,255,0.75);
      font-family:'DM Sans',sans-serif;
    }

    /* Toolbar */
    .sl2-toolbar {
      background:${WHITE}; border-radius:16px;
      box-shadow:0 4px 20px rgba(0,0,0,.06);
      padding:16px 20px; margin-bottom:20px;
      display:flex; align-items:center; gap:12px; flex-wrap:wrap;
    }
    .sl2-root .sl2-search.ant-input-affix-wrapper {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important; flex:1; min-width:200px;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .sl2-root .sl2-search.ant-input-affix-wrapper:hover,
    .sl2-root .sl2-search.ant-input-affix-wrapper-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .sl2-root .sl2-search .ant-input { font-family:'DM Sans',sans-serif !important; font-size:13px !important; }

    .sl2-btn {
      height:40px !important; padding:0 16px !important; border-radius:10px !important;
      font-size:13px !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; background:${WHITE} !important;
      color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important;
      display:flex !important; align-items:center !important; gap:6px !important;
    }
    .sl2-btn:hover { border-color:${TEAL} !important; color:${TEAL} !important; }

    /* Table */
    .sl2-table-wrap {
      background:${WHITE}; border-radius:20px;
      box-shadow:0 4px 20px rgba(0,0,0,.06); overflow:hidden;
    }
    .sl2-root .ant-table-thead > tr > th {
      background:${SURFACE} !important; color:${SLATE_MID} !important;
      font-size:11px !important; font-weight:700 !important;
      letter-spacing:.5px; text-transform:uppercase;
      border-bottom:1.5px solid #f1f5f9 !important;
      font-family:'Sora',sans-serif !important; padding:14px 16px !important;
    }
    .sl2-root .ant-table-tbody > tr > td {
      font-size:13px !important; font-family:'DM Sans',sans-serif !important;
      color:${SLATE_MID} !important; padding:14px 16px !important;
      border-bottom:1px solid #f8fafc !important;
    }
    .sl2-root .ant-table-tbody > tr:hover > td { background:#f0fdfb !important; }
    .sl2-root .ant-pagination { padding:16px !important; }
    .sl2-root .ant-pagination-item-active { border-color:${TEAL} !important; }
    .sl2-root .ant-pagination-item-active a { color:${TEAL} !important; }

    /* Badges */
    .sl2-badge {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
      display:inline-flex; align-items:center; gap:5px;
    }

    /* Block button */
    .sl2-block-btn {
      height:30px !important; padding:0 12px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important; font-family:'DM Sans',sans-serif !important;
      border:1.5px solid #ef4444 !important; color:#ef4444 !important;
      background:${WHITE} !important; transition:all .2s !important;
    }
    .sl2-block-btn:hover { background:#ef4444 !important; color:${WHITE} !important; }

    /* Blocked tag */
    .sl2-blocked-tag {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
      background:#fee2e2; color:#dc2626;
      display:inline-flex; align-items:center; gap:5px;
    }

    /* Animations */
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .sl2-fade   { animation:fadeUp .5s ease both; }
    .sl2-fade-1 { animation-delay:.05s; }
    .sl2-fade-2 { animation-delay:.12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ──────────────────────────────────────────── */
function StaffList() {
  const [users,        setUsers]        = useState([]);
  const [searchTerm,   setSearchTerm]   = useState("");
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo,   setSortedInfo]   = useState({});
  const dispatch = useDispatch();

  useEffect(() => { injectStyles(); }, []);

  const getUserData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const response = await axios.get("/api/admin/get-all-staff", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (response.data.success) setUsers(response.data.data);
      else message.error(response.data.message);
    } catch {
      dispatch(hideLoading());
      message.error("Error fetching staff data");
    }
  }, [dispatch]);

  useEffect(() => { getUserData(); }, [getUserData]);

  const handleBlock = async (userId) => {
    try {
      dispatch(showLoading());
      const response = await axios.put(
        `/api/admin/block-user/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        message.success("User blocked successfully");
        setUsers(prev => prev.map(u => u._id === userId ? { ...u, isBlocked: true } : u));
      } else {
        message.error(response.data.message);
      }
    } catch {
      dispatch(hideLoading());
      message.error("Error blocking user");
    }
  };

  const handleChange = (_, filters, sorter) => { setFilteredInfo(filters); setSortedInfo(sorter); };
  const clearFilters = () => setFilteredInfo({});
  const clearAll     = () => { setFilteredInfo({}); setSortedInfo({}); setSearchTerm(""); };

  /* ── Summary counts ── */
  const counts = users.reduce((acc, u) => {
    acc.total = (acc.total || 0) + 1;
    acc[u.role] = (acc[u.role] || 0) + 1;
    if (u.isBlocked) acc.blocked = (acc.blocked || 0) + 1;
    return acc;
  }, {});

  /* ── Columns ── */
  const columns = [
    {
      title: "Staff Member",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === "name" ? sortedInfo.order : null,
      ellipsis: true,
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar
            size={34}
            style={{
              background: record.isBlocked
                ? "#fee2e2"
                : `linear-gradient(135deg,${EMERALD},${TEAL})`,
              color: record.isBlocked ? "#dc2626" : WHITE,
              fontSize: 13, fontWeight: 700, flexShrink: 0,
            }}
          >
            {name?.[0]?.toUpperCase()}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 13, color: record.isBlocked ? MUTED : SLATE, display: "block" }}>
              {name}
            </Text>
            <Text style={{ fontSize: 11, color: MUTED }}>{record.email}</Text>
          </div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      sorter: (a, b) => (a.phone || "").localeCompare(b.phone || ""),
      sortOrder: sortedInfo.columnKey === "phone" ? sortedInfo.order : null,
      ellipsis: true,
      render: text => <Text style={{ fontSize: 13, color: SLATE_MID }}>{text || "—"}</Text>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      filters: [
        { text: "Admin",   value: "admin"   },
        { text: "Doctor",  value: "doctor"  },
        { text: "Staff",   value: "staff"   },
        { text: "Patient", value: "patient" },
      ],
      filteredValue: filteredInfo.role || null,
      onFilter: (value, record) => record.role === value,
      sorter: (a, b) => a.role.localeCompare(b.role),
      sortOrder: sortedInfo.columnKey === "role" ? sortedInfo.order : null,
      ellipsis: true,
      render: (role) => {
        const cfg = getRoleCfg(role);
        return (
          <span className="sl2-badge" style={{ background: cfg.bg, color: cfg.color }}>
            {cfg.icon}
            {role?.charAt(0).toUpperCase() + role?.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => record.isBlocked ? (
        <span className="sl2-badge" style={{ background: "#fee2e2", color: "#dc2626" }}>
          <StopOutlined />Blocked
        </span>
      ) : (
        <span className="sl2-badge" style={{ background: TEAL_LIGHT, color: EMERALD }}>
          <CheckCircleOutlined />Active
        </span>
      ),
      filters: [
        { text: "Active",  value: false },
        { text: "Blocked", value: true  },
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (value, record) => record.isBlocked === value,
      width: 120,
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      sortOrder: sortedInfo.columnKey === "createdAt" ? sortedInfo.order : null,
      render: date => <Text style={{ fontSize: 12, color: MUTED }}>{moment(date).format("MMM D, YYYY")}</Text>,
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) =>
        record.isBlocked ? (
          <span className="sl2-blocked-tag">
            <StopOutlined />Blocked
          </span>
        ) : (
          <Button
            className="sl2-block-btn"
            icon={<StopOutlined />}
            onClick={() => handleBlock(record._id)}
          >
            Block
          </Button>
        ),
    },
  ];

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="sl2-root">
        <div className="sl2-wrap">

          {/* Hero */}
          <div className="sl2-hero sl2-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="sl2-eyebrow"><TeamOutlined style={{ marginRight: 5 }} />Administration</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Staff List
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                View and manage all internal staff members, doctors, and admins
              </Text>
              <div className="sl2-hero-badges">
                <div className="sl2-hero-badge"><TeamOutlined style={{ marginRight: 5 }} />{counts.total || 0} Total</div>
                {counts.staff   && <div className="sl2-hero-badge" style={{ background: "#fef9c3cc", color: "#b45309" }}>🧑‍💼 {counts.staff} Staff</div>}
                {counts.doctor  && <div className="sl2-hero-badge" style={{ background: `${TEAL_LIGHT}cc`, color: EMERALD }}>🩺 {counts.doctor} Doctors</div>}
                {counts.admin   && <div className="sl2-hero-badge" style={{ background: "#f5f3ffcc", color: "#7c3aed" }}>⚙ {counts.admin} Admins</div>}
                {counts.blocked && <div className="sl2-hero-badge" style={{ background: "#fee2e2cc", color: "#dc2626" }}>🚫 {counts.blocked} Blocked</div>}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="sl2-toolbar sl2-fade sl2-fade-1">
            <Input
              className="sl2-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search by email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
            <Button className="sl2-btn" icon={<FilterOutlined />} onClick={clearFilters}>
              Clear Filters
            </Button>
            <Button className="sl2-btn" icon={<ClearOutlined />} onClick={clearAll}>
              Reset All
            </Button>
          </div>

          {/* Table */}
          <div className="sl2-table-wrap sl2-fade sl2-fade-2">
            <Table
              columns={columns}
              dataSource={filteredUsers.map(u => ({ ...u, key: u._id || u.email }))}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              onChange={handleChange}
              rowKey="_id"
              scroll={{ x: 900 }}
            />
          </div>

        </div>
      </div>
    </Layout>
  );
}

export default StaffList;